/**
 * Checkout Redux State Module
 *
 * This module manages the state for the checkout application, which handles:
 * - Shopping cart/basket management
 * - Shipping and billing address entry
 * - Payment method selection and processing
 * - Order placement and payment completion
 * - Gift with purchase configuration
 * - Delivery date prediction and selection
 *
 * State Structure:
 * The checkout state is divided into four main slices:
 *
 * - `data`: API-loaded data (basket, countries, states, shipping methods, payment methods, etc.)
 * - `form`: User input state (addresses, payment methods, current step, form field values)
 * - `derived`: Computed values (shipping costs, totals, merged basket acknowledgments)
 * - `gifts`: Gift with purchase configuration (selected products, option values, bundles)
 */
import { PayloadAction, combineReducers, createSlice } from "@reduxjs/toolkit";
import { type Draft, castDraft, current } from "immer";

import { IAddress, IAddressState } from "../../models/address";
import {
    IBasket,
    IBasketLinkShippingMethod,
    IConcreteBundle,
    IOptionValues,
    IProduct,
    IWishListLine,
} from "../../models/catalogue";
import { IOrder, IOrderResponse, IPaymentStates } from "../../models/checkout";
import { FinancingPlan } from "../../models/financing";
import { isoProductAPIURL } from "../../models/nominals";
import { IShippingMethod } from "../../models/shipping";
import {
    ISetOptionValuePayload,
    setOptionValue as updateOptionValue,
} from "../../store/reducer-utils";
import { objectKeys } from "../../utils/functional";
import {
    ConfigureGiftStatus,
    DEFAULT_COUNTRY,
    DEFAULT_PHONE_NUMBER,
    LIST_PREVIOUSLY_IN_BASKET,
    PRIMARY_PAYMENT_METHOD_KEY,
    PredictedDeliveryDateStatus,
    SECONDARY_PAYMENT_METHOD_KEY,
    Step,
} from "./constants";
import { defaultMethodOrder, defaults } from "./defaults";
import {
    IPayment,
    IPaymentPartial,
    IReduxAPIData,
    IReduxDerivedData,
    IReduxFormState,
} from "./reducers.interfaces";
import {
    loadAssistedUser,
    loadBasket,
    loadConcreteBundles,
    loadCountries,
    loadEmailAddress,
    loadPaymentMethods,
    loadPredictedDeliveryDate,
    loadShippingAndBillingAddresses,
    loadUserAddresses,
    loadUserConfigurableBundles,
    savePreferredDeliveryDate,
} from "./thunks";

// ============================================================================
// Helper Functions for Data Slice
// ============================================================================

const applyBasketUpdate = (state: IReduxAPIData, basket: IBasket) => {
    const bundles: {
        [lineURL: string]: readonly IConcreteBundle[] | undefined;
    } = {};
    const shippingMethods: {
        [lineURL: string]: readonly IBasketLinkShippingMethod[] | undefined;
    } = {};

    // Extract bundle and shipping method data from the current basket
    if (state.basket) {
        state.basket.lines.forEach((line) => {
            bundles[isoProductAPIURL.unwrap(line.product.url)] = line.bundles;
            shippingMethods[isoProductAPIURL.unwrap(line.product.url)] =
                line.shipping_methods;
        });
    }

    // Inject bundle and shipping method data back into the new basket
    // Note: We must create new line objects since basket comes from the action
    // payload and is frozen by Immer
    const updatedLines = basket.lines.map((line) => ({
        ...line,
        bundles: bundles[isoProductAPIURL.unwrap(line.product.url)] || [],
        shipping_methods:
            shippingMethods[isoProductAPIURL.unwrap(line.product.url)] || [],
    }));

    return {
        ...state,
        basket: {
            ...basket,
            lines: updatedLines,
        },
    };
};

const applyOrderUpdate = (state: IReduxAPIData, order: IOrderResponse) => {
    order = { ...order };

    return {
        ...state,
        order: order,
    };
};

const injectBundle = (state: IReduxAPIData, bundle: IConcreteBundle) => {
    if (!state.basket) {
        return state;
    }
    return {
        ...state,
        basket: {
            ...state.basket,
            lines: state.basket.lines.map((line) => {
                if (bundle.triggering_product !== line.product.id) {
                    return line;
                }
                return {
                    ...line,
                    bundles: line.bundles
                        .filter((b) => {
                            return b.id !== bundle.id;
                        })
                        .concat([bundle]),
                };
            }),
        },
    };
};

const injectShippingMethods = (
    state: IReduxAPIData,
    methods: ReadonlyArray<IShippingMethod>,
) => {
    let basket = state.basket;
    if (state.basket) {
        basket = {
            ...state.basket,
            lines: state.basket.lines.map((line) => {
                return {
                    ...line,
                    shipping_methods: methods.map((method) => {
                        const submethod = method.lines.find((sub) => {
                            return sub.line_url === line.url;
                        });
                        return {
                            code: method.code,
                            price: method.price.incl_tax || "0.00",
                            name: submethod
                                ? submethod.method.name
                                : method.name,
                            description: submethod
                                ? submethod.method.description
                                : "",
                        };
                    }),
                };
            }),
        };
    }
    return {
        ...state,
        shipping_methods: methods,
        shipping_method_errors: [] as string[],
        basket: basket,
    };
};

const injectShippingMethodErrors = (state: IReduxAPIData, errors: string[]) => {
    return {
        ...state,
        shipping_methods: [] as ReadonlyArray<IShippingMethod>,
        shipping_method_errors: errors,
    };
};

const updateAPIDataFields = (
    state: IReduxAPIData,
    updatedFields: Partial<IReduxAPIData>,
) => {
    return {
        ...state,
        ...updatedFields,
    };
};

const applyWishlistUpdate = (
    state: IReduxAPIData,
    code: string,
    lines: readonly IWishListLine[],
) => {
    state = {
        ...state,
        wishlists: {
            ...state.wishlists,
            [code]: lines,
        },
    };
    return state;
};

const applyPaymentStatesReset = (state: IReduxAPIData) => {
    state = {
        ...state,
        payment_states: defaults.data.payment_states,
    };
    return state;
};

const importShippingAddr = (
    addr: Partial<IAddress>,
): Partial<Draft<IReduxFormState>> => {
    const fields: Partial<Draft<IReduxFormState>> = {};

    if (!objectKeys(addr).length) {
        fields.current_step = Step.SHIPPING_ADDRESS;
        return fields;
    }

    // Backend can persist historical "notes" on saved addresses,
    // which can contain stale content from a prior purchase/session.
    // So, we shouldn't blindly import that value.
    const disallowedKeys = new Set<string>(["notes"]);
    objectKeys(addr).forEach((key) => {
        if (disallowedKeys.has(key)) {
            return;
        }
        const fieldKey = `shipping_${key}` as keyof IReduxFormState;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (fields as any)[fieldKey] = addr[key];
    });

    return fields;
};

const importBillingAddr = (
    addr: Partial<IAddress>,
): Partial<Draft<IReduxFormState>> => {
    const fields: Partial<Draft<IReduxFormState>> = {};

    if (!objectKeys(addr).length) {
        return fields;
    }

    objectKeys(addr).forEach((key) => {
        const fieldKey = `billing_${key}` as keyof IReduxFormState;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (fields as any)[fieldKey] = addr[key];
    });

    return fields;
};

// ============================================================================
// Helper Functions for Form Slice
// ============================================================================

const cloneShippingAddressToBillingAddress = (state: IReduxFormState) => {
    const newState: IReduxFormState = {
        ...state,
        billing_address_url: state.shipping_address_url,
        billing_first_name: state.shipping_first_name,
        billing_last_name: state.shipping_last_name,
        billing_line1: state.shipping_line1,
        billing_line2: state.shipping_line2,
        billing_postcode: state.shipping_postcode,
        billing_line4: state.shipping_line4,
        billing_state: state.shipping_state,
        billing_phone_number: state.shipping_phone_number,
        billing_country: state.shipping_country,
    };
    return newState;
};

const clearBillingAddress = (state: IReduxFormState) => {
    const newState: IReduxFormState = {
        ...state,
        billing_address_url: "",
        billing_first_name: "",
        billing_last_name: "",
        billing_line1: "",
        billing_line2: "",
        billing_postcode: "",
        billing_line4: "",
        billing_state: "",
        billing_phone_number: DEFAULT_PHONE_NUMBER,
        billing_country: DEFAULT_COUNTRY,
    };
    return newState;
};

const updateFormFields = (
    state: IReduxFormState,
    updatedFields: Partial<IReduxFormState> | Partial<Draft<IReduxFormState>>,
) => {
    state = { ...state, ...updatedFields };

    if (state.billing_addr_is_shipping_addr) {
        state = cloneShippingAddressToBillingAddress(state);
    }

    return state;
};

const setUseShippingAddrAsBilling = (
    state: IReduxFormState,
    billingIsShipping: boolean,
) => {
    const newState: IReduxFormState = {
        ...state,
        billing_addr_is_shipping_addr: billingIsShipping,
    };
    if (newState.billing_addr_is_shipping_addr) {
        return cloneShippingAddressToBillingAddress(newState);
    }
    return clearBillingAddress(newState);
};

const calculatePaymentMethodAmounts = (state: IReduxFormState) => {
    if (SECONDARY_PAYMENT_METHOD_KEY in state.payment_methods) {
        return {
            ...state,
            payment_methods: {
                ...state.payment_methods,
                [PRIMARY_PAYMENT_METHOD_KEY]: {
                    ...state.payment_methods[PRIMARY_PAYMENT_METHOD_KEY],
                    pay_balance: false,
                },
                [SECONDARY_PAYMENT_METHOD_KEY]: {
                    ...state.payment_methods[SECONDARY_PAYMENT_METHOD_KEY],
                    pay_balance: true,
                },
            },
        };
    }
    return {
        ...state,
        payment_methods: {
            ...state.payment_methods,
            [PRIMARY_PAYMENT_METHOD_KEY]: {
                ...state.payment_methods[PRIMARY_PAYMENT_METHOD_KEY],
                pay_balance: true,
            },
        },
    };
};

const resetPaymentMethods = (
    state: IReduxFormState,
    methods: readonly IPayment["method_type"][],
) => {
    const default_methods = { ...defaults.form.payment_methods };
    // if form primary method is 'default', set to first available default
    if (default_methods[PRIMARY_PAYMENT_METHOD_KEY].method_type === "default") {
        for (const method_type of defaultMethodOrder) {
            if (methods.includes(method_type)) {
                switch (method_type) {
                    case "bluefin": {
                        default_methods[PRIMARY_PAYMENT_METHOD_KEY] = {
                            payment_data: "",
                            ...default_methods[PRIMARY_PAYMENT_METHOD_KEY],
                            method_type: method_type,
                        };
                        break;
                    }
                    case "cybersource": {
                        default_methods[PRIMARY_PAYMENT_METHOD_KEY] = {
                            card_type: "",
                            card_number: "",
                            card_expiration: "",
                            card_cvc: "",
                            ...default_methods[PRIMARY_PAYMENT_METHOD_KEY],
                            method_type: method_type,
                        };
                        break;
                    }
                    case "cybersource-microform": {
                        default_methods[PRIMARY_PAYMENT_METHOD_KEY] = {
                            transient_token: "",
                            card_type: "",
                            card_number: "",
                            ...default_methods[PRIMARY_PAYMENT_METHOD_KEY],
                            method_type: method_type,
                        };
                        break;
                    }
                    case "financing": {
                        default_methods[PRIMARY_PAYMENT_METHOD_KEY] = {
                            new_financing_account: null,
                            financing_account: "",
                            financing_plan: null,
                            has_agreed: false,
                            has_esigned: false,
                            ...default_methods[PRIMARY_PAYMENT_METHOD_KEY],
                            method_type: method_type,
                        };
                        break;
                    }
                    case "cash": {
                        default_methods[PRIMARY_PAYMENT_METHOD_KEY] = {
                            ...default_methods[PRIMARY_PAYMENT_METHOD_KEY],
                            method_type: method_type,
                        };
                        break;
                    }
                    case "pay-later": {
                        default_methods[PRIMARY_PAYMENT_METHOD_KEY] = {
                            ...default_methods[PRIMARY_PAYMENT_METHOD_KEY],
                            method_type: method_type,
                        };
                        break;
                    }
                }
                break;
            }
        }
    }
    state = {
        ...state,
        payment_methods: default_methods,
    };
    return calculatePaymentMethodAmounts(state);
};

const applyAddPaymentMethod = (
    state: IReduxFormState,
    methodKey: string,
    method: IPayment,
) => {
    state = {
        ...state,
        payment_methods: {
            ...state.payment_methods,
            [methodKey]: method,
        },
    };
    return calculatePaymentMethodAmounts(state);
};

const applyRemovePaymentMethod = (
    state: IReduxFormState,
    methodKey: string,
) => {
    const methods = { ...state.payment_methods };
    delete methods[methodKey];
    state = {
        ...state,
        payment_methods: methods,
    };
    return calculatePaymentMethodAmounts(state);
};

const updatePaymentMethodFields = (
    state: IReduxFormState,
    methodKey: string,
    fields: IPaymentPartial,
) => {
    const method = state.payment_methods[methodKey];
    if (!method) {
        // TODO(i18n): This error message is for developers (thrown error), not user-facing, so OK to leave in English
        throw new Error(`Payment method key ${methodKey} does not exist.`);
    }
    const newFields = fields as Partial<typeof method>;
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    const mergedData: any = { ...method, ...newFields };
    state = {
        ...state,
        payment_methods: {
            ...state.payment_methods,
            [methodKey]: mergedData,
        },
    };
    return calculatePaymentMethodAmounts(state);
};

// ============================================================================
// Data Slice
// ============================================================================

const dataSlice = createSlice({
    name: "checkout/data",
    initialState: defaults.data,
    reducers: {
        updateOrder: (state, action: PayloadAction<IOrderResponse>) => {
            return applyOrderUpdate(state, action.payload);
        },

        updateShippingStates: (
            state,
            action: PayloadAction<IAddressState[]>,
        ) => {
            return updateAPIDataFields(state, {
                shipping_states: action.payload,
            });
        },

        updateBillingStates: (
            state,
            action: PayloadAction<IAddressState[]>,
        ) => {
            return updateAPIDataFields(state, {
                billing_states: action.payload,
            });
        },

        updateShippingMethods: (
            state,
            action: PayloadAction<{
                methods: ReadonlyArray<IShippingMethod>;
                errors: string[];
            }>,
        ) => {
            if (action.payload.errors.length > 0) {
                return injectShippingMethodErrors(state, action.payload.errors);
            }
            return injectShippingMethods(state, action.payload.methods);
        },

        updatePaymentStates: (state, action: PayloadAction<IPaymentStates>) => {
            return updateAPIDataFields(state, {
                payment_states: action.payload,
            });
        },

        resetPaymentStates: (state) => {
            return applyPaymentStatesReset(state);
        },

        updatePaymentError: (state, action: PayloadAction<string>) => {
            return updateAPIDataFields(state, {
                payment_error: action.payload,
            });
        },

        updateFinancingPlans: (
            state,
            action: PayloadAction<FinancingPlan[]>,
        ) => {
            return updateAPIDataFields(state, {
                financing_plans: action.payload,
            });
        },

        beginCompleteDeferredPayment: (
            state,
            action: PayloadAction<{ order: IOrder }>,
        ) => {
            return updateAPIDataFields(state, {
                deferredPaymentOrder: action.payload.order,
            });
        },
    },
    extraReducers: (builder) => {
        // loadBasket thunk
        builder.addCase(loadBasket.fulfilled, (state, action) => {
            const newState1 = applyBasketUpdate(state, action.payload.basket);
            const newState2 = applyWishlistUpdate(
                newState1,
                LIST_PREVIOUSLY_IN_BASKET,
                action.payload.previousItemsList,
            );
            const newState3 = updateAPIDataFields(newState2, {
                financing_plans: action.payload.financingPlans,
            });
            return castDraft(newState3);
        });

        // loadConcreteBundles thunk
        builder.addCase(loadConcreteBundles.fulfilled, (state, action) => {
            let newState = current(state) as IReduxAPIData;
            action.payload.forEach((bundle) => {
                newState = castDraft(injectBundle(newState, bundle));
            });
            return castDraft(newState);
        });

        // loadCountries thunk
        builder.addCase(loadCountries.fulfilled, (state, action) => {
            return updateAPIDataFields(state, {
                countries: action.payload.countries,
            });
        });

        // loadUserAddresses thunk
        builder.addCase(loadUserAddresses.fulfilled, (state, action) => {
            return updateAPIDataFields(state, {
                addresses: action.payload,
            });
        });

        // loadPaymentMethods thunk
        builder.addCase(loadPaymentMethods.fulfilled, (state, action) => {
            let newState = updateAPIDataFields(state, {
                payment_methods: action.payload,
            });
            // Clear any payment errors
            newState = updateAPIDataFields(newState, {
                payment_error: "",
            });
            return castDraft(newState);
        });

        // loadAssistedUser thunk
        builder.addCase(loadAssistedUser.fulfilled, (state, action) => {
            if (!action.payload) {
                return state;
            }
            return updateAPIDataFields(state, {
                assisted_user: action.payload,
            });
        });

        // loadPredictedDeliveryDate thunk
        builder.addCase(loadPredictedDeliveryDate.pending, (state) => {
            return updateAPIDataFields(state, {
                predicted_delivery_date: {
                    status: PredictedDeliveryDateStatus.LOADING,
                },
            });
        });
        builder.addCase(
            loadPredictedDeliveryDate.fulfilled,
            (state, action) => {
                return updateAPIDataFields(
                    state,
                    action.payload.error
                        ? {
                              predicted_delivery_date: {
                                  status: PredictedDeliveryDateStatus.ERROR,
                                  data: action.payload.payload,
                              },
                          }
                        : {
                              predicted_delivery_date: {
                                  status: PredictedDeliveryDateStatus.LOADED,
                                  data: action.payload.payload,
                              },
                          },
                );
            },
        );

        // savePreferredDeliveryDate thunk
        builder.addCase(
            savePreferredDeliveryDate.fulfilled,
            (state, action) => {
                return {
                    ...state,
                    preferred_date: action.payload.date
                        ? action.payload.date.toISOString()
                        : null,
                };
            },
        );
    },
});

// ============================================================================
// Form Slice
// ============================================================================

const formSlice = createSlice({
    name: "checkout/form",
    initialState: defaults.form,
    reducers: {
        setFields: (state, action: PayloadAction<Partial<IReduxFormState>>) => {
            return updateFormFields(state, action.payload);
        },

        useShippingAddrAsBillingAddr: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            return setUseShippingAddrAsBilling(state, action.payload);
        },

        resetPaymentMethods: (
            state,
            action: PayloadAction<{
                methods: readonly IPayment["method_type"][];
            }>,
        ) => {
            return resetPaymentMethods(state, action.payload.methods);
        },

        addPaymentMethod: (
            state,
            action: PayloadAction<{ method_key: string; method: IPayment }>,
        ) => {
            return applyAddPaymentMethod(
                state,
                action.payload.method_key,
                action.payload.method,
            );
        },

        removePaymentMethod: (state, action: PayloadAction<string>) => {
            return applyRemovePaymentMethod(state, action.payload);
        },

        setPaymentMethodFields: (
            state,
            action: PayloadAction<{
                method_key: string;
                fields: Partial<IPayment>;
            }>,
        ) => {
            return updatePaymentMethodFields(
                state,
                action.payload.method_key,
                action.payload.fields,
            );
        },
    },
    extraReducers: (builder) => {
        // loadCountries thunk
        builder.addCase(loadCountries.fulfilled, (state, action) => {
            return updateFormFields(state, {
                shipping_country: action.payload.initialCountryURL,
                billing_country: action.payload.initialCountryURL,
            });
        });

        // loadAssistedUser thunk
        builder.addCase(loadAssistedUser.fulfilled, (state, action) => {
            if (!action.payload || !action.payload.email) {
                return state;
            }
            return updateFormFields(state, {
                guest_email: action.payload.email,
            });
        });

        // loadEmailAddress thunk
        builder.addCase(loadEmailAddress.fulfilled, (state, action) => {
            if (!action.payload.email) {
                return state;
            }
            return updateFormFields(state, {
                guest_email: action.payload.email,
            });
        });

        // loadShippingAndBillingAddresses thunk
        builder.addCase(
            loadShippingAndBillingAddresses.fulfilled,
            (state, action) => {
                const {
                    shipping,
                    billing,
                    billingIsShipping,
                    shouldSetStepToShippingAddress,
                } = action.payload;

                let newState = { ...state };

                // Import shipping address fields
                const shippingFields = importShippingAddr(shipping);
                newState = updateFormFields(newState, shippingFields);

                // Set billing address mode
                newState = setUseShippingAddrAsBilling(
                    newState,
                    billingIsShipping,
                );

                // Import billing address fields if not same as shipping
                if (!billingIsShipping) {
                    const billingFields = importBillingAddr(billing);
                    newState = updateFormFields(newState, billingFields);
                }

                // Set step if needed
                if (shouldSetStepToShippingAddress) {
                    newState = updateFormFields(newState, {
                        current_step: Step.SHIPPING_ADDRESS,
                    });
                }

                return newState;
            },
        );

        // loadPaymentMethods thunk - reset form payment methods when available methods are loaded
        builder.addCase(loadPaymentMethods.fulfilled, (state, action) => {
            return resetPaymentMethods(state, action.payload);
        });
    },
});

// ============================================================================
// Derived Slice
// ============================================================================

const derivedSlice = createSlice({
    name: "checkout/derived",
    initialState: defaults.derived,
    reducers: {
        setDerivedFields: (
            state,
            action: PayloadAction<Partial<IReduxDerivedData>>,
        ) => {
            return {
                ...state,
                ...action.payload,
            };
        },

        acknowledgeMergedBasket: (state, action: PayloadAction<number>) => {
            return {
                ...state,
                acknowledged_merged_baskets:
                    state.acknowledged_merged_baskets.concat(action.payload),
            };
        },
    },
});

// ============================================================================
// Gifts Slice
// ============================================================================

const giftsSlice = createSlice({
    name: "checkout/gifts",
    initialState: defaults.gifts,
    reducers: {
        setConfigureGiftStatus: (
            state,
            action: PayloadAction<ConfigureGiftStatus>,
        ) => {
            state.status = action.payload;
        },

        setAddToBasketErrorModalState: (
            state,
            action: PayloadAction<{ isOpen: boolean; reason: string }>,
        ) => {
            state.addToBasketError = {
                addToBasketErrorOpen: action.payload.isOpen,
                addToBasketErrorReason: action.payload.reason,
            };
        },

        setGiftRootProduct: (
            state,
            action: PayloadAction<{
                giftID: string;
                bundleID: number;
                rootProduct: IProduct;
            }>,
        ) => {
            // Find default variant
            const defaultVariant =
                action.payload.rootProduct.children.length > 0
                    ? action.payload.rootProduct.children[0]
                    : action.payload.rootProduct;
            // Set default variant
            const options =
                defaultVariant.attributes.product_options?.value || [];
            const optionValues: IOptionValues = {};
            options.forEach((code) => {
                const attr = defaultVariant.attributes[code];
                if (attr) {
                    optionValues[code] = attr.value;
                }
            });
            state.gifts[action.payload.giftID] = {
                bundleID: action.payload.bundleID,
                selectedRootProduct: action.payload.rootProduct.id,
                optionValues: castDraft(optionValues),
            };
        },

        setGiftOptionValue: (
            state,
            action: PayloadAction<
                ISetOptionValuePayload & {
                    giftID: string;
                }
            >,
        ) => {
            if (state.gifts[action.payload.giftID]) {
                state.gifts[action.payload.giftID].optionValues = castDraft(
                    updateOptionValue(
                        state.gifts[action.payload.giftID].optionValues,
                        action.payload,
                    ),
                );
            }
        },
    },
    extraReducers: (builder) => {
        // loadUserConfigurableBundles thunk
        builder.addCase(
            loadUserConfigurableBundles.fulfilled,
            (state, action) => {
                state.userConfigurableBundles = castDraft(action.payload);
                state.status = ConfigureGiftStatus.LOADED;
            },
        );
    },
});

// ============================================================================
// Combined Reducer
// ============================================================================

export const reducers = combineReducers({
    data: dataSlice.reducer,
    form: formSlice.reducer,
    derived: derivedSlice.reducer,
    gifts: giftsSlice.reducer,
});

// ============================================================================
// Export Action Creators
// ============================================================================

// Data slice actions
export const {
    updateOrder,
    updateShippingStates,
    updateBillingStates,
    updateShippingMethods,
    updatePaymentStates,
    resetPaymentStates,
    updatePaymentError,
    updateFinancingPlans,
    beginCompleteDeferredPayment,
} = dataSlice.actions;

// Form slice actions
export const {
    setFields,
    useShippingAddrAsBillingAddr,
    resetPaymentMethods: resetPaymentMethodsForm,
    addPaymentMethod,
    removePaymentMethod,
    setPaymentMethodFields,
} = formSlice.actions;

// Derived slice actions
export const { setDerivedFields, acknowledgeMergedBasket } =
    derivedSlice.actions;

// Gifts slice actions
export const {
    setConfigureGiftStatus,
    setAddToBasketErrorModalState,
    setGiftRootProduct,
    setGiftOptionValue,
} = giftsSlice.actions;
